'use client';

import FormLabel from '@c/forms/controls/FormLabel';
import { useQuery } from '@tanstack/react-query';
import Select from '@ui/Select';
import {
  getMainCatsClient,
  getSubcatsClient,
} from '@util/firestore/categories';
import { CategoryDocument } from 'models/category';
import { Fragment } from 'react';
import { useCreateListing } from '../_context';

export const CreateListingStepFour = () => {
  const { form, setCurrentStep, categoryInfo, setCategoryInfo } =
    useCreateListing();

  const { data: mainCategories } = useQuery({
    queryKey: ['mainCategories'],
    queryFn: () => getMainCatsClient(true),
  });

  const ids = categoryInfo.map((c) => c?.id);

  const { data: cat1s } = useQuery({
    queryKey: ['categories', 1, ids[0]],
    queryFn: () => getSubcatsClient(1, ids[0], true, true),
    enabled: !!ids[0],
  });

  const { data: cat2s } = useQuery({
    queryKey: ['categories', 2, ids[1]],
    queryFn: () => getSubcatsClient(2, ids[1], true, true),
    enabled: !!ids[1],
  });

  const { data: cat3s } = useQuery({
    queryKey: ['categories', 3, ids[2]],
    queryFn: () => getSubcatsClient(3, ids[2], true, true),
    enabled: !!ids[2],
  });

  const { data: cat4s } = useQuery({
    queryKey: ['categories', 4, ids[3]],
    queryFn: () => getSubcatsClient(4, ids[3], true, true),
    enabled: !!ids[3],
  });

  const formatCategoryToOptions = (op: CategoryDocument[] | undefined) => {
    if (op === undefined) {
      return [];
    }

    return op
      ?.filter((o) => !o.is_seo_only)
      .map((section) => {
        return {
          id: section.id,
          label: section.app_desc || section.name,
          value: section.id,
        };
      });
  };

  const renderSubcategorySelect = (
    options: CategoryDocument[] | undefined,
    index: number
  ) => {
    if (!options?.length) return null;

    return (
      <Select
        key={`subcategory-select-${index}`}
        options={formatCategoryToOptions(options)}
        value={categoryInfo[index]?.id ?? ''}
        onChange={(option) => updateCategory(index, option.value, options)}
        allowEmpty={false}
        className="mb-7 px-8 py-5"
        placeholder="Select a subcategory"
      />
    );
  };

  const updateCategory = (
    index: number,
    value: string,
    options: CategoryDocument[]
  ) => {
    const next = [...categoryInfo.slice(0, index)];
    next[index] = options.find((o) => o.id === value)!;
    setCategoryInfo(next);
    form.clearErrors('category');
  };

  const isComplete = () => {
    const levels = [cat1s, cat2s, cat3s, cat4s];

    for (let i = 0; i < levels.length; i++) {
      if (levels[i]?.length && !categoryInfo[i + 1]) {
        return false;
      }
    }

    return true;
  };

  const goFoward = () => {
    if (!isComplete()) {
      form.setError('category', {
        type: 'required',
        message: 'Please select all required categories',
      });
      return;
    }

    const lastCat = categoryInfo[categoryInfo.length - 1];

    form.setValue('category', lastCat.product_cats.category!);
    form.setValue('category1', lastCat.product_cats.category1);
    form.setValue('category2', lastCat.product_cats.category2);
    form.setValue('category3', lastCat.product_cats.category3);
    form.setValue(
      'categories',
      [
        lastCat.product_cats.category!,
        lastCat.product_cats.category1!,
        lastCat.product_cats.category2!,
        lastCat.product_cats.category3!,
      ].filter(Boolean)
    );
    setCurrentStep((prev) => prev + 1);
  };

  const selectOptions = formatCategoryToOptions(mainCategories);

  const goBack = () => {
    form.setValue('category1', null);
    form.setValue('category2', null);
    form.setValue('category3', null);
    form.clearErrors('category');
    form.clearErrors('category1');
    form.clearErrors('category2');
    form.clearErrors('category3');

    const categoriesWithStepThree = [
      'categories_new/race-cars-for-sale',
      'categories_new/parts',
    ];

    if (categoriesWithStepThree.includes(categoryInfo[0]?.id)) {
      setCurrentStep((prev) => prev - 1);
      return;
    }

    setCurrentStep(0);
  };

  return (
    <div className="py-12 flex h-[calc(100vh-65px)] w-full flex-col justify-between px-8 md:mx-auto md:pt-20 md:max-w-[60rem] md:justify-start md:gap-10">
      <div>
        <h1 className="mb-12 text-[3rem] font-bold text-black md:mb-20">
          Categories
        </h1>
        <FormLabel
          value="Category"
          className="text-[1.875rem] font-semibold"
          required
        >
          <Select
            options={selectOptions}
            className="px-8 py-5"
            value={categoryInfo[0]?.id ?? ''}
            onChange={(option) => {
              const main = mainCategories?.find((o) => o.id === option.value)!;
              setCategoryInfo([main]);
              form.clearErrors('category');
            }}
            placeholder="Race cars"
            allowEmpty={false}
          />
        </FormLabel>
        {cat1s?.length ? (
          <Fragment>
            <div className="my-8 h-px w-full bg-[#d9d9d9] lg:my-12" />
            <FormLabel value="Subcategory" className="!text-[1.8rem]">
              <div className="flex flex-col gap-y-6">
                {[cat1s, cat2s, cat3s, cat4s].map((cat, i) =>
                  renderSubcategorySelect(cat, i + 1)
                )}
              </div>
            </FormLabel>
          </Fragment>
        ) : null}
      </div>
      <div className="flex w-full flex-col items-center gap-[1rem]">
        {form.formState.errors?.category?.message && (
          <p className="text-red-500">
            {form.formState.errors?.category?.message}
          </p>
        )}
        <div className="flex w-full justify-between gap-6 md:pt-8 md:gap-8">
          <button
            className="h-[44px] w-full rounded-full border border-black text-center text-[1.875rem] font-semibold"
            type="button"
            onClick={goBack}
          >
            Back
          </button>

          <button
            className="h-[44px] w-full rounded-full bg-brand-secondary text-center text-[1.875rem] font-semibold text-white"
            type="button"
            onClick={goFoward}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};
